import secondFilterImg from '@/assets/images/secondFilter.svg';
import NewTag from '@/components/category/NewTag';
import ImpTracker from '@/components/common/ImpTracker';
import Skeleton from '@/components/common/Skeleton';
import SortFilter from '@/components/common/SortFilter';
import Swiper, { SwiperSlide } from '@/components/common/Swiper';
import RewardsDealTag from '@/components/rewards/RewardsDealTag';
import { BrandMod, CategoryMod } from '@/constants/track';
import { useScrollData } from '@/hooks/useTouchData';
import useClickActionT2 from '@/tracks/useClickActionT2';
import useImpressionT2 from '@/tracks/useImpressionT2';
import { CategoryType, CuisineCategory, FilterType, SecondCuisineCategory } from '@/types/category.d';
import isEmpty from '@/utils/isEmpty';
import { getPathNoLangOnClient, toString as queryToString } from '@/utils/url';
import { CheckmarkOutline } from '@weee-fe/kunlunicon';
import { CroppedImage } from '@weee-fe/module';
import classnames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { getPageEventNameV2 } from 'tracks/tracker';
import { WeeeIcon } from '../Icon';
import ExplorerFilter from './ExplorerFilter';

interface Props {
  mainCategoryList?: CuisineCategory[];
  /**当前分类 */
  mainCategory?: CuisineCategory | CategoryType;
  handleMainCategory?: (item: CuisineCategory, nextLink?: string) => void;
  secondCategoryList: SecondCuisineCategory[];
  /**当前二级分类 */
  secondCategory: SecondCuisineCategory;
  handleSecondCategory: (item: SecondCuisineCategory) => void;
  /**正在获取数据 */
  loading?: boolean;
  /**是否展示以及分类展位图 */
  showMainCategorySkeleton?: boolean;
  /**是否获取以及分类 */
  changeCategory?: boolean;
  /**是否加载二级分类 */
  secondLoading?: boolean;
  /**过滤数量 */
  filterCount: number;
  listRef: React.MutableRefObject<HTMLDivElement>;
  handleFilterClick?: any;
  showMainCategory?: boolean;
  buildPathName?: (item: ObjectType, isMainCategory: boolean) => string;
  sortFilterProps?: ObjectType;
  isFromBrand?: boolean;
  showShadow?: boolean;
  secondCategoryPreventDefault?: boolean;
  isNeedReplaceUrl?: boolean;
  filterBtnClassName?: string;
  trackCtx?: Record<string, string>;
  showBadgeOnFilter?: boolean;
  optimizeForGlobalHeader?: boolean;
}

const SKELETONS = [1, 1, 1, 1, 1, 1];

function HorizontalSlideList(props: Props) {
  const {
    mainCategory,
    mainCategoryList,
    secondCategory,
    secondCategoryList,
    showShadow = false,
    changeCategory,
    filterCount,
    handleMainCategory,
    handleSecondCategory,
    listRef,
    showMainCategory = true,
    buildPathName,
    sortFilterProps,
    isFromBrand,
    isNeedReplaceUrl = false,
    filterBtnClassName,
    trackCtx,
    showBadgeOnFilter,
    optimizeForGlobalHeader
  } = props;
  const sortFilterRef = useRef(null);
  const explorerFilterRef = useRef(null);
  const { t, lang } = useTranslation('common');
  const router = useRouter();
  const { query } = router;
  const { scrolling, direction, position } = useScrollData();
  const scrollRef = useRef<HTMLDivElement>(null);
  const categoryRef = useRef<HTMLDivElement>(null);
  const secondCategoryRef = useRef<HTMLDivElement>(null);
  const activeIndex = useMemo(() => {
    return mainCategoryList.findIndex(item => {
      return item.key === mainCategory?.key;
    });
  }, [mainCategory?.key, JSON.stringify(mainCategoryList)]);
  const [loadingMain, setLoadingMain] = useState(true);
  const [selectExplorer, setSelectExplorer] = useState({});
  const { ellipseImpression, filterBtnImpression } = useImpressionT2();
  const { ellipseClick, filterBtnClick, customButtonClick } = useClickActionT2();
  useEffect(() => {
    if (!scrolling || !direction.y) return;
    if (!categoryRef.current || !listRef.current) return;
    // 滑动过小不做变化
    if (position.y < 5) {
      categoryRef.current.style.transform = 'translateY(0px)';
      if (showShadow) categoryRef.current.style.boxShadow = '';
      listRef.current.style.transform = `translateY(0px)`;
      return;
    }
    if (direction.y === 'down') {
      categoryRef.current.style.transform = `translateY(-${scrollRef.current?.clientHeight}px)`;
      if (showShadow) categoryRef.current.style.boxShadow = '0px 1px 12px rgba(0, 0, 0, 0.12)';
      listRef.current.style.transform = `translateY(-${scrollRef.current?.clientHeight}px)`;
    }

    if (direction.y === 'up') {
      categoryRef.current.style.transform = 'translateY(0)';
      if (showShadow) categoryRef.current.style.boxShadow = '';
      listRef.current.style.transform = `translateY(0)`;
    }
  }, [direction.y, showShadow]);

  useEffect(() => {
    if (changeCategory || !secondCategory?.catalogue_num) return;
    let timer = setTimeout(() => {
      if (!secondCategoryRef.current) return;
      const ele = document.getElementById(`secondCategory-${secondCategory?.catalogue_num}`) as HTMLElement;
      if (ele && ele.offsetLeft > secondCategoryRef.current.scrollLeft + secondCategoryRef.current.clientWidth / 2) {
        ele && ele.scrollIntoView({ block: 'center', inline: 'center' });
      }
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, [JSON.stringify(secondCategory), changeCategory]);

  useEffect(() => {
    setLoadingMain(false);
  }, []);

  const genCategoryHref = (cateKey, isMainCategory, item?) => {
    let { sort, filters, slug, ...queries } = query;
    const { filterArr } = sortFilterProps;

    const newQuery: ObjectType = queries;
    if (!!sort && !isMainCategory) newQuery.sort = sort;
    if (!filters) newQuery.filters = {};
    else {
      try {
        newQuery.filters = JSON.parse(filters as string);
      } catch (error) {
        newQuery.filters = {};
      }
    }
    let pathname = buildPathName?.(item, isMainCategory) || router.pathname;
    let fullUrl = '';
    if (isMainCategory) {
      if (pathname.startsWith('/cuisines')) {
        newQuery.filterCuisine = cateKey;
      } else if (pathname.startsWith('/category')) {
        newQuery.filter_sub_category = cateKey;
      }
      delete newQuery.filters.catalogue_num;
    } else {
      newQuery.filters.catalogue_num = cateKey;
    }
    if (Object.keys(newQuery.filters).length) {
      if (isMainCategory) {
        let _filter = {};
        Object.keys(newQuery.filters).forEach(key => {
          let isRemained = filterArr.findIndex(item => item.shareable && item.property_key === key) > -1;
          if (isRemained) {
            _filter[key] = newQuery.filters[key];
          }
        });
        if (Object.keys(_filter).length) {
          newQuery.filters = JSON.stringify(_filter);
        } else {
          delete newQuery.filters;
        }
      } else {
        newQuery.filters = JSON.stringify(newQuery.filters);
      }
    } else {
      delete newQuery.filters;
    }
    fullUrl = `${pathname}${queryToString(newQuery)}`;
    return fullUrl;
  };

  const renderExplorerStore = () => {
    const { filterArr } = sortFilterProps;
    const showOutResult = filterArr.filter(item => item.show_out);

    if (!showOutResult?.length) {
      return null;
    }
    return (
      <div className="flex items-center justify-start relative pl-4 pb-2">
        <div
          id="explorerCategoryFilter"
          className="z-20"
          onClick={() => {
            filterBtnClick({
              mod_pos: 2,
              co: {
                button_nm: 'filter_buttons',
                button_pos: 0
              },
              ctx: trackCtx
            });
            sortFilterRef.current?.show();
          }}>
          {changeCategory ? (
            <Skeleton.Button
              style={{ width: '32px', position: 'relative', height: '32px', top: 0, right: 0 }}
              shape="circle"
            />
          ) : (
            <div className="rounded-[13px] bg-surface-100-hairline px-2 h-[30px] flex items-center">
              <img className="w-4 h-4" src={secondFilterImg} alt="second filter image" />
            </div>
          )}
          {!changeCategory && filterCount > 0 && (
            <div className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-white text-[13px] bg-primary-1 absolute top-1 left-10">
              {filterCount}
            </div>
          )}
        </div>
        {/* explorer type */}
        {changeCategory ? (
          <div className="box-border w-full flex overflow-x-scroll touch-auto -ml-2.5 pl-2 pr-4">
            <Skeleton.Button
              style={{
                width: '80px',
                position: 'relative',
                height: '32px',
                top: 0,
                right: 0,
                marginLeft: 12,
                borderRadius: 20
              }}
            />
          </div>
        ) : (
          <div className="box-border w-full flex overflow-x-scroll touch-auto -ml-2.5 pl-2 pr-4">
            {showOutResult?.map((item, index) => {
              const currentSelectTagId = sortFilterProps.explorerFilterData
                ? sortFilterProps.explorerFilterData[item.property_key]?.map(item => item.value_key)?.toString()
                : '';
              const isBoolItem = item.property_show_type === FilterType.boolean;
              const isBoolItemSelected = item.property_values?.reduce((prev, curr) => {
                return prev || !!curr?.selected;
              }, false);

              return (
                <ImpTracker
                  key={index}
                  impTrackingT2={() => {
                    filterBtnImpression({
                      mod_nm: isFromBrand ? BrandMod.filterBtn : CategoryMod.filterBtn,
                      mod_pos: 2,
                      co: {
                        button_nm: item.property_key,
                        button_pos: index,
                        // is_selected: secondCategory.catalogue_num === item.catalogue_num,
                        is_selected: !!currentSelectTagId,
                        url: router?.asPath
                      },
                      ctx: {
                        ...trackCtx,
                        tag_id: currentSelectTagId
                      }
                    });
                  }}>
                  <div
                    onClick={() => {
                      customButtonClick({
                        mod_nm: isFromBrand ? BrandMod.filterBtn : CategoryMod.filterBtn,
                        mod_pos: 2,
                        co: {
                          target_nm: item.property_key,
                          target_type: 'filter_button',
                          click_type: 'view',
                          target_pos: 0
                        },
                        ctx: {
                          ...trackCtx,
                          tag_id: currentSelectTagId
                        }
                      });
                      if (isBoolItem) {
                        sortFilterProps?.applyExplorerFilterCallback(null, {
                          property_show_type: item.property_show_type,
                          filter_type: item.property_key,
                          filter_arr: [
                            {
                              property_show_type: item.property_show_type,
                              filter_type: item.property_key,
                              value_key: !isBoolItemSelected,
                              value_name: ''
                            }
                          ]
                        });
                      } else {
                        setSelectExplorer(item);
                        explorerFilterRef.current?.show();
                      }
                    }}
                    className={twMerge(
                      'rounded-[13px] bg-surface-100-hairline px-2.5 ml-2 flex items-center enki-body-2xs-medium h-[30px] whitespace-nowrap'
                    )}>
                    {item.property_name}

                    {isBoolItem && isBoolItemSelected && <CheckmarkOutline fontSize={16} className="ml-1" />}
                    {!isBoolItem && (
                      <>
                        {sortFilterProps?.explorerFilterData &&
                        sortFilterProps?.explorerFilterData[item.property_key]?.length ? (
                          <span className="inline-block w-[14px] h-[14px] rounded-full !leading-[14px] enki-body-2xs-strong text-white bg-black text-center p-0.5 ml-1">
                            {sortFilterProps?.explorerFilterData[item.property_key]?.length}
                          </span>
                        ) : null}
                        <WeeeIcon
                          style={{ fontSize: 20 }}
                          className="text-surface-100-fg-default ml-1"
                          type="iconarrow-down"
                        />
                      </>
                    )}
                  </div>
                </ImpTracker>
              );
            })}
            {Object.keys(sortFilterProps?.explorerFilterData || {})?.map(key => {
              const _data = sortFilterProps?.explorerFilterData[key];
              if (_data?.length) {
                return _data?.map((dItem, dIndex) => {
                  return (
                    <div
                      key={`${dIndex}-${key}`}
                      onClick={() => {
                        //多选选择
                        if (
                          dItem.property_show_type === FilterType.multiple ||
                          dItem.property_show_type === FilterType.single
                        ) {
                          customButtonClick({
                            mod_nm: 'filter_buttons',
                            mod_pos: 2,
                            co: {
                              target_nm: dItem?.value_key,
                              target_type: 'filter_button',
                              target_pos: dIndex,
                              click_type: 'unselect'
                            },
                            ctx: trackCtx
                          });
                          sortFilterProps?.applyExplorerFilterCallback(null, {
                            property_show_type: dItem.property_show_type,
                            filter_type: dItem.filter_type,
                            filter_arr: _data.filter(_sItem => {
                              return _sItem.value_key !== dItem.value_key;
                            })
                          });
                        }
                      }}
                      className="rounded-[13px] bg-[#EBF4FF] px-2.5 ml-2 flex items-center enki-body-2xs-medium text-[#1C2954] h-[30px] whitespace-nowrap">
                      {dItem?.value_name}
                      <WeeeIcon
                        style={{ fontSize: 20 }}
                        className="text-surface-100-fg-default ml-1"
                        type="iconTimes"
                      />
                    </div>
                  );
                });
              }
            })}
            {/* 如果是单选需要全部放出来 可进行反选, todo */}
            {/* {showOutResult
                ?.filter(item => item.property_show_type === FilterType.boolean)
                ?.map((dItem, dIndex) => {
                  return (
                    <div
                      key={`${dIndex}-${dItem.value_key}`}
                      onClick={() => {
                        //单选选择
                      }}
                      className="rounded-[13px] bg-[#EBF4FF] px-2.5 ml-2 flex items-center enki-body-2xs-medium text-[#1C2954] h-[30px] whitespace-nowrap">
                      {dItem?.property_name}
                      <WeeeIcon
                        style={{ fontSize: 12, marginTop: 2, zoom: 0.8 }}
                        className="text-surface-100-fg-default ml-1"
                        type="iconcheck-thin"
                      />
                    </div>
                  );
                })} */}
          </div>
        )}
      </div>
    );
  };

  const renderMainCategoryList = () => {
    if (loadingMain || mainCategoryList?.length === 0) {
      return (
        <div className="w-full h-[92px] overflow-x-scroll">
          {SKELETONS.map((item, index) => {
            return (
              <div
                className="inline-block text-center first:ml-5 px-1 last:mr-5"
                key={`main-category-skeleton-${index}`}>
                <div className="w-[60px] h-[60px] rounded-full bg-[#f3f3f3] skeleton" />
                <div className="w-[60px] !h-4 !block !rounded-[10px] mt-1 bg-[#f3f3f3] skeleton" />
              </div>
            );
          })}
        </div>
      );
    }
    return (
      <Swiper
        activeIndex={activeIndex}
        slidesPerView="auto"
        className="!pl-4 h-[92px] !pr-4"
        centeredSlides={true}
        centeredSlidesBounds={true}>
        {mainCategoryList.map((item, sub) => {
          const categoryIconItem = (
            <ImpTracker
              impTrackingT2={() => {
                let pageKey = getPageEventNameV2(getPathNoLangOnClient()) || '';
                ellipseImpression({
                  mod_nm: CategoryMod.ellipse,
                  mod_pos: 0,
                  co: {
                    ellipse_label: item?.key,
                    ellipse_pos: sub,
                    ellipse_type: pageKey.replace('mweb_', ''),
                    is_new: item?.is_new,
                    url: router?.asPath
                  },
                  ctx: trackCtx
                });
              }}
              clickTrackingT2={() => {
                let pageKey = getPageEventNameV2(getPathNoLangOnClient()) || '';
                ellipseClick({
                  mod_nm: CategoryMod.ellipse,
                  mod_pos: 0,
                  co: {
                    ellipse_label: item?.key,
                    ellipse_pos: sub,
                    ellipse_type: pageKey.replace('mweb_', ''),
                    url: router?.asPath
                  },
                  ctx: {
                    filter_sub_category: item?.key,
                    catalogue_num: router.query.catalogue_num || 'all',
                    sort: router.query.sort || 'recommend',
                    filters: router.query.filters ? JSON.parse(router.query.filters as string) : null,
                    ...trackCtx
                  }
                });
              }}>
              <div className="inline-block">
                <div className="w-[60px] h-[60px] flex justify-center items-center touch-auto relative mx-auto my-0">
                  <CroppedImage
                    url={item.key === mainCategory?.key ? item.active_img_url : item.img_url}
                    configs={{
                      renderWidth: 60,
                      renderHeight: 60
                    }}
                    width={60}
                    height={60}
                    alt="product image"
                    placeholder={<div className="w-[60px] h-[60px] bg-[#f3f3f3] skeleton rounded-full" />}
                  />
                  {item?.is_new && <NewTag className="top-1" />}
                  {!isEmpty(item?.category_label) && <RewardsDealTag data={item?.category_label} />}
                </div>
                <div
                  className={twMerge(
                    classnames(
                      'touch-auto text-center break-normal enki-body-2xs line-clamp-2 text-surface-100-fg-minor whitespace-normal mt-1',
                      {
                        '!leading-[120%]': lang === 'en',
                        ['text-surface-100-fg-default']: item.key === mainCategory?.key
                      }
                    )
                  )}>
                  {item.key === mainCategory?.key ? <h1 className="enki-body-2xs">{item.name}</h1> : item.name}
                </div>
              </div>
            </ImpTracker>
          );
          //lunar year requirement
          if (+item.type === 4) {
            return (
              <SwiperSlide key={`main-category-${item.key}`} className="!w-auto max-w-[76px] box-border mx-1.5">
                <Link href={item.url} shallow>
                  <a className="flex flex-col items-center justify-center" aria-label={`search category ${item.key}`}>
                    {categoryIconItem}
                  </a>
                </Link>
              </SwiperSlide>
            );
          }
          return (
            <SwiperSlide key={`main-category-${item.key}`} className="!w-auto max-w-[76px] box-border mx-1.5">
              <Link href={genCategoryHref(item.key, true, item) || ''} replace={isNeedReplaceUrl} shallow>
                <a
                  className="flex flex-col items-center justify-center"
                  onClick={() => {
                    handleMainCategory(item, genCategoryHref(item.key, true, item));
                    secondCategoryRef?.current?.scrollTo(0, 0);
                  }}
                  aria-label={`search category ${item?.key}`}>
                  {categoryIconItem}
                </a>
              </Link>
            </SwiperSlide>
          );
        })}
      </Swiper>
    );
  };

  const checkShowSortFilter = () => {
    //如果有需要展示在外面的筛选项, 需要隐藏第一级的筛选项
    const { filterArr } = sortFilterProps;
    return !filterArr?.some(item => item.show_out);
  };

  const renderFilterBadge = () => {
    if (changeCategory) {
      return null;
    }
    if (filterCount > 0) {
      if (showBadgeOnFilter) {
        return (
          <div className="h-[18px] min-w-[18px] rounded-full bg-primary-1 text-[13px] text-white absolute top-1.5 left-5">
            {filterCount}
          </div>
        );
      }
      return (
        <div
          className="h-[18px] min-w-[18px] px-1 box-border rounded-full enki-body-2xs-strong text-surface-100-bg flex bg-primary-1 justify-center items-center -top-1 -right-1 absolute"
          style={{ background: mainCategory?.color }}>
          {filterCount}
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className="sticky z-[100] w-full top-12 bg-surface-100-bg"
      style={{
        transition: 'all 0.3s ease-out',
        transform: 'translateY(0px)'
      }}
      id="category_filter"
      ref={categoryRef}
      data-role="main-category-body">
      {showMainCategory && (
        <div className="w-auto whitespace-nowrap overflow-hidden" data-role="main-category">
          <div className="pt-1" ref={scrollRef}>
            {renderMainCategoryList()}
          </div>
        </div>
      )}
      <div className="w-auto flex items-center pl-4 pt-4 pb-2" data-role="second-category">
        {checkShowSortFilter() && (
          <div className="w-8 relative z-[1] rounded-full bg-surface-100-bg" data-role="second-category-filter">
            <div
              id="secondCategoryFilter"
              className={twMerge('w-8 h-8 relative flex items-center justify-center', filterBtnClassName)}
              onClick={() => {
                filterBtnClick({
                  mod_pos: 1,
                  co: {
                    button_nm: 'filter_buttons',
                    button_pos: 0
                  },
                  ctx: trackCtx
                });
                sortFilterRef.current?.show();
              }}>
              {changeCategory ? (
                <Skeleton.Button
                  style={{ width: '32px', position: 'relative', height: '32px', top: 0, right: 0 }}
                  shape="circle"
                />
              ) : (
                <div className="rounded-[13px] bg-surface-100-hairline px-2 h-[30px] flex items-center">
                  <img className="w-4 h-4" src={secondFilterImg} alt="second filter img image" />
                </div>
              )}
              {renderFilterBadge()}
            </div>
          </div>
        )}
        <div
          className={twMerge(
            'box-border flex-1 grow overflow-x-scroll touch-auto relative -ml-2.5 pl-4',
            !checkShowSortFilter() && 'pl-2.5'
          )}
          ref={secondCategoryRef}>
          <div className="w-fit flex flex-nowrap items-center touch-auto">
            {changeCategory ? (
              <Skeleton.Button style={{ marginRight: '6px', borderRadius: '13px', height: '30px' }} />
            ) : (
              <ImpTracker
                height={30}
                impTrackingT2={() => {
                  filterBtnImpression({
                    mod_nm: isFromBrand ? BrandMod.filterBtn : CategoryMod.filterBtn,
                    mod_pos: isFromBrand ? 0 : 1,
                    co: {
                      button_nm: 'all',
                      button_pos: 0,
                      is_selected: secondCategory.catalogue_num === '',
                      url: router?.asPath
                    },
                    ctx: trackCtx
                  });
                }}
                clickTrackingT2={() => {
                  filterBtnClick({
                    mod_nm: isFromBrand ? BrandMod.filterBtn : CategoryMod.filterBtn,
                    mod_pos: isFromBrand ? 0 : 1,
                    co: {
                      button_nm: 'all',
                      button_pos: 0,
                      url: router?.asPath
                    },
                    ctx: trackCtx
                  });
                }}>
                <Link
                  replace={isNeedReplaceUrl}
                  shallow
                  href={genCategoryHref('', false, secondCategoryList?.[0]) || ''}
                  scroll={!props.secondCategoryPreventDefault}>
                  <a
                    className={twMerge(
                      classnames(
                        'text-surface-100-fg-minor touch-auto flex justify-center items-center whitespace-nowrap mr-1.5 px-2.5 py-0 enki-body-2xs-medium rounded-[13px] border border-solid border-surface-100-hairline',
                        {
                          'text-surface-100-bg border-none bg-btn-secondary-bg': secondCategory.catalogue_num === ''
                        }
                      )
                    )}
                    style={{
                      height: 28,
                      background: secondCategory.catalogue_num === '' ? mainCategory?.color : 'transparent'
                    }}
                    onClick={() => {
                      handleSecondCategory({ catalogue_num: '', catalogue_name: '' });
                    }}
                    aria-label={`search all ${mainCategory}`}>
                    {t('search_all')}
                  </a>
                </Link>
              </ImpTracker>
            )}
            {changeCategory
              ? SKELETONS.map((item, index) => {
                  return (
                    <Skeleton.Button
                      key={'skeleton_second_item' + index}
                      style={{ marginRight: '6px', width: '100px', height: '30px', borderRadius: '13px' }}
                    />
                  );
                })
              : secondCategoryList.map((item, sub) => {
                  return (
                    <ImpTracker
                      key={`secondCategoryListItem:${sub}`}
                      impTrackingT2={() => {
                        filterBtnImpression({
                          mod_nm: isFromBrand ? BrandMod.filterBtn : CategoryMod.filterBtn,
                          mod_pos: isFromBrand ? 0 : 1,
                          co: {
                            button_nm: item?.catalogue_num,
                            button_pos: sub + 1,
                            is_selected: secondCategory.catalogue_num === item.catalogue_num,
                            url: router?.asPath
                          },
                          ctx: trackCtx
                        });
                      }}
                      clickTrackingT2={() => {
                        filterBtnClick({
                          mod_nm: isFromBrand ? BrandMod.filterBtn : CategoryMod.filterBtn,
                          mod_pos: isFromBrand ? 0 : 1,
                          co: {
                            button_nm: item?.catalogue_num,
                            button_pos: sub + 1,
                            url: router?.asPath
                          },
                          ctx: {
                            filter_sub_category: router.query.filterCuisine || null,
                            catalogue_num: item?.catalogue_num,
                            sort: router.query.sort || 'recommend',
                            filters: router.query.filters ? JSON.parse(router.query.filters as string) : null,
                            ...trackCtx
                          }
                        });
                      }}>
                      <Link
                        replace={isNeedReplaceUrl}
                        shallow
                        href={genCategoryHref(item.catalogue_num, false, item) || ''}
                        scroll={!props.secondCategoryPreventDefault}>
                        <a
                          id={`secondCategory-${item.catalogue_num}-${sub}`}
                          className={twMerge(
                            classnames(
                              'text-surface-100-fg-minor touch-auto flex justify-center items-center whitespace-nowrap mr-1.5 px-2.5 py-0 enki-body-2xs-medium rounded-[13px] border border-solid border-surface-100-hairline',
                              {
                                'text-surface-100-bg border-none bg-btn-secondary-bg':
                                  secondCategory.catalogue_num === item.catalogue_num
                              }
                            )
                          )}
                          style={{
                            height: 28,
                            background:
                              secondCategory.catalogue_num === item.catalogue_num ? mainCategory?.color : 'transparent'
                          }}
                          onClick={event => {
                            if (props.secondCategoryPreventDefault) {
                              event.preventDefault();
                            } else {
                              event.currentTarget.scrollIntoView({ block: 'center', inline: 'center' });
                            }
                            handleSecondCategory(item);
                          }}
                          aria-label={`search second category ${item?.catalogue_num}`}>
                          {secondCategory.catalogue_num === item.catalogue_num ? (
                            <h2 className="enki-body-2xs-medium m-0">{item.catalogue_name}</h2>
                          ) : (
                            item.catalogue_name
                          )}
                        </a>
                      </Link>
                    </ImpTracker>
                  );
                })}
          </div>
        </div>
      </div>
      {/* Explorer store */}
      {renderExplorerStore()}
      <SortFilter {...sortFilterProps} trackCtx={trackCtx} ref={sortFilterRef} />
      <ExplorerFilter
        curFilter={selectExplorer}
        explorerFilterData={sortFilterProps?.explorerFilterData}
        applyExplorerFilterCallback={sortFilterProps?.applyExplorerFilterCallback}
        trackCtx={trackCtx}
        ref={explorerFilterRef}
      />
    </div>
  );
}

export default HorizontalSlideList;
